import { useSearchParams, useNavigate, Outlet } from 'react-router-dom';
import { OnboardingStatus, OnboardingStep } from '@interfaces/models';
import { PaymentStatus } from '@shared/components/payu';
import { onboardingStatus } from '@api/onboarding';
import { useQuery } from '@tanstack/react-query';
import { useEffect, Suspense, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@shared/hooks';
import { ssManager } from '@shared/utils';
import { toast } from '@ui/components';
import { Routes } from '@shared/enums';

import { OnboardingLayout } from './onboarding-layout';

export const OnboardingRoute: FC = () => {
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [params] = useSearchParams();
  const paymentStatus = params.get('status') as PaymentStatus;

  const { data, isLoading, isError } = useQuery({
    queryKey: ['onboarding', 'status'],
    queryFn: onboardingStatus,
  });

  useEffect(() => {
    if (isError) toast.error(t('ONBOARDING.STATUS_ERROR'));
  }, [isError, t]);

  useEffect(() => {
    if (!data) return;

    if (user?.onboardingStatus !== OnboardingStatus.IN_PROGRESS) {
      navigate(Routes.ROOT);
      return;
    }

    // TODO: This is temporary solution. We need to handle payment status in a better way.
    if (!!paymentStatus && data.step === OnboardingStep.TOP_UP_BALANCE) {
      if (paymentStatus === 'SUCCESS') navigate(Routes.TOP_UP_BALANCE_SUCCESS, { replace: true });
      if (paymentStatus === 'FAILURE' || paymentStatus === 'TIMEOUT')
        navigate(Routes.TOP_UP_BALANCE_FAILED, { replace: true });
      return;
    }

    /* if user is onboarding, redirect to onboarding step */
    navigate(`${Routes.ONBOARDING}/${Routes[data.step] || Routes.ADD_VEHICLES}`, { replace: true });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, user]);

  useEffect(() => {
    // Remove companyId on all onboarding routes. It's not needed here.
    ssManager.remove('COMPANY_ID');
  }, [isLoading]);

  if (isLoading) {
    return <Suspense />;
  }

  return (
    <OnboardingLayout {...data!}>
      <Outlet context={{ hasSubscriptionsAssigned: data?.hasSubscriptionsAssigned }} />
    </OnboardingLayout>
  );
};
